import {Grid, IconButton,Typography, Box} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { observer } from "mobx-react-lite";
import SmartAppHotbar from "./SmartAppHotbar";
import { AppStorage } from "e.sic-platform.webclient/src/services/app/AppStorage";

export interface ISmartApp {
  appStorage: AppStorage,
  actionBar?: any;
  hideToolbar?: boolean;
  hideImage?: boolean;
  smallTitle?: boolean;
  hideRedo?: boolean;
  // Optional panel rendered to the RIGHT of the app description, in the space the
  // 45%-wide description text leaves empty (Inspection Insights puts its order &
  // process box there). Stacks under the description on narrow screens.
  headerAside?: any;
  // Replaces the app's one-paragraph description (descriptionkey) with custom content.
  headerDescription?: any;
  children: any;
}

export default observer(function SmartApp(props: ISmartApp) {
  const { appStorage } = props;

  return (
    <>
      {!props.hideToolbar && <SmartAppHotbar {...props}/>}
      <Grid container xs={12} sx={{ py: 2, px: { xs: 2, md: 4, lg: 5 }, maxWidth: "100%", overflowX: "clip", boxSizing: "border-box" }}>
        {!props.hideImage && (
        <Grid container xs = {3}  sx={{display: {lg: "block", xs: "none"}}}>
        <Grid item xs={12} lg ={12} className="centered" >
            {appStorage.image && (
              appStorage.route === "/inspection-insights" ? (
                <Box
                  component="img"
                  src={appStorage.image}
                  alt=""
                  sx={{
                    width: 240,
                    height: 240,
                    borderRadius: 2,
                    objectFit: "cover",
                    border: "4px solid #fff",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
                    mt: 1,
                  }}
                />
              ) : (
                <img
                  src={appStorage.image}
                  height={300}
                  style={{ maxWidth: "100%", marginTop: 4 }}
                />
              )
            )}
          </Grid>
        </Grid>
        )}
        <Grid
          item
          xs={12}
          lg={props.hideImage ? 12 : 9}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "stretch", lg: "flex-start" },
            gap: { lg: 3 },
            minHeight: { lg: 240 },
            pl: { lg: 3 },
          }}
        >
          {/* Title + description keep their own vertically-centred column, so an app
              without a headerAside renders exactly as before. */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flex: 1,
              minWidth: 0,
              minHeight: { lg: 240 },
            }}
          >
            <Typography
              variant={props.smallTitle ? "h5" : "h3"}
              sx={{
                fontWeight: 600,
                mb: 2,
                textAlign: { xs: "center", lg: "left" },
              }}
            >
              {translationService.translate(appStorage.titleKey)}
            </Typography>
            {props.headerDescription ?? (
            <Typography
              variant="body1"
              color="GrayText"
              sx={{
                lineHeight: 1.7,
                // Without an aside the text is capped at 45% so it doesn't run the full
                // width; with one, the aside itself sets where the text stops.
                maxWidth: { lg: props.headerAside ? "100%" : "45%" },
                textAlign: { xs: "center", lg: "justify" },
              }}
            >
              {translationService.translate(appStorage.descriptionkey)}
            </Typography>
            )}
          </Box>
          {/* Top-right of the header: no shrink, level with the title rather than the
              description, and sized to END exactly where the results view's video column ends,
              so the two right edges line up down the page.

              Deriving it (W = this outer container's content width): the video is a `lg={4}`
              item of a `spacing={2}` grid inside the results step's `px: 2` wrapper, which makes
              its content `W/3 - 16px` wide with its right edge 16px inside the container. This
              Box instead lives in the `lg={9}` description column, whose content width is
              `P = 0.75W - 24px` (the `pl: 3` above) — and W/3 - 16px expressed against P is
              `4P/9 - 16px/3`, hence the calc. The right margin reproduces the video column's
              16px inset. Hiding the header image changes P, so that case keeps the old fixed
              width rather than silently misaligning. */}
          {props.headerAside && (
            <Box
              sx={{
                width: {
                  xs: "100%",
                  lg: props.hideImage ? 460 : "calc(400% / 9 - 16px / 3)",
                },
                mr: { xs: 0, lg: props.hideImage ? 0 : 2 },
                flexShrink: 0,
                alignSelf: "flex-start",
                mt: { xs: 2, lg: 0 },
              }}
            >
              {props.headerAside}
            </Box>
          )}
        </Grid>
        <Box
          className="smart-app-collapse-toggle"
          display="flex"
          sx={{ mb: 2, cursor: "pointer" }}
          onClick={() => appStorage.toggleCollapsed()}
        >
          <IconButton>
            {appStorage.collapsed ? (
              <ExpandMoreIcon
                sx={{
                  transform: "rotate(-90deg)",
                  fontWeight: "bold",
                  fontSize: "2rem",
                }}
              />
            ) : (
              <ExpandLessIcon
                sx={{
                  transform: "rotate(-90deg)",
                  fontWeight: "bold",
                  fontSize: "2rem",
                }}
              />
            )}
          </IconButton>
        </Box>

        <Grid container rowSpacing={2} sx={{ mt: 0 }}>
          {props.children}
        </Grid>
      </Grid>
    </>
  );
});
